import { c as _c } from "react/compiler-runtime";
import { type JsonSerializable } from '@copilotkit/react-core/v2';
import { useMemo } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { FEATURE_FLAGS, useFeatureFlag } from 'shared/featureFlag';
import { useUserInstructions, useWorkspaceInstructions } from 'shared/copilot/api/instructions';
import { useCopilotAgentContext } from 'shared/copilot/hooks/useCopilotAgentContext';
import { useQueryUserProfile } from 'src/profile/api';
import useMember from 'src/workspace/hooks/useMember';
import { type CopilotUserAgentContext, type CopilotWorkspaceAgentContext } from './types';

/**
 * Registers always-on CopilotKit agent context for the current workspace and user.
 *
 * Distinct from page context (`useRegisterCopilotPageContext` / page agent context):
 * these payloads stay available on every surface regardless of which page tools are mounted.
 */
export const useCopilotGlobalAgentContext = () => {
  const $ = _c(45);
  const {
    currentClient
  } = useCurrentClient();
  const {
    currentUser
  } = useAuth();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      throwOnError: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: userDetails
  } = useQueryUserProfile(currentUser?.id, t0);
  const {
    member
  } = useMember();
  const {
    value: isCopilotSidenavEnabled
  } = useFeatureFlag(FEATURE_FLAGS.copilotSidenav);
  const {
    value: isCopilotFullscreenEnabled
  } = useFeatureFlag(FEATURE_FLAGS.copilot);
  const isCopilotEnabled = isCopilotSidenavEnabled || isCopilotFullscreenEnabled;
  const clientId = currentClient.id;
  const userId = currentUser?.id;
  const {
    data: userInstructions
  } = useUserInstructions(clientId, userId, isCopilotEnabled);
  const {
    data: workspaceInstructions
  } = useWorkspaceInstructions(clientId, isCopilotEnabled);
  let t1;
  bb0: {
    const hasClientId = Boolean(currentClient.id);
    if (!hasClientId) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[1] !== currentClient.currency || $[2] !== currentClient.id || $[3] !== currentClient.name) {
      t2 = {
        id: currentClient.id,
        name: currentClient.name,
        currency: currentClient.currency
      };
      $[1] = currentClient.currency;
      $[2] = currentClient.id;
      $[3] = currentClient.name;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t2;
  }
  const workspaceContext = t1;
  let t2;
  bb1: {
    if (userDetails == null || member == null) {
      t2 = null;
      break bb1;
    }
    const t3 = userDetails.phone !== "" ? userDetails.phone : null;
    const t4 = userDetails.companyName !== "" ? userDetails.companyName : null;
    const t5 = userDetails.jobTitle !== "" ? userDetails.jobTitle : null;
    let t6;
    if ($[5] !== userDetails.hasInternalFeatureAccess || $[6] !== userDetails.showInternalFeatures) {
      t6 = userDetails.hasInternalFeatureAccess ? {
        hasInternalFeatureAccess: true as const,
        showInternalFeatures: userDetails.showInternalFeatures
      } : {};
      $[5] = userDetails.hasInternalFeatureAccess;
      $[6] = userDetails.showInternalFeatures;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== member.role || $[9] !== t3 || $[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== userDetails.email || $[14] !== userDetails.id || $[15] !== userDetails.name) {
      t7 = {
        id: userDetails.id,
        name: userDetails.name,
        email: userDetails.email,
        workspaceRole: member.role,
        phone: t3,
        companyName: t4,
        jobTitle: t5,
        ...t6
      };
      $[8] = member.role;
      $[9] = t3;
      $[10] = t4;
      $[11] = t5;
      $[12] = t6;
      $[13] = userDetails.email;
      $[14] = userDetails.id;
      $[15] = userDetails.name;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t2 = t7;
  }
  const userContext = t2;
  let t3;
  if ($[17] !== userInstructions?.content) {
    t3 = () => {
      const content = userInstructions?.content?.trim() ?? "";
      if (content === "") {
        return null;
      }
      return {
        content
      };
    };
    $[17] = userInstructions?.content;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  userInstructions?.content;
  let t4;
  if ($[19] !== t3) {
    t4 = t3();
    $[19] = t3;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const userInstructionsContext = t4;
  workspaceInstructions?.content;
  let t5;
  bb2: {
    let t6;
    if ($[21] !== workspaceInstructions?.content) {
      t6 = workspaceInstructions?.content?.trim() ?? "";
      $[21] = workspaceInstructions?.content;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    const content_0 = t6;
    if (content_0 === "") {
      t5 = null;
      break bb2;
    }
    let t7;
    if ($[23] !== content_0) {
      t7 = {
        content: content_0
      };
      $[23] = content_0;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    t5 = t7;
  }
  const workspaceInstructionsContext = t5;
  let t6;
  if ($[25] !== workspaceContext) {
    t6 = workspaceContext ?? {};
    $[25] = workspaceContext;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const t7 = t6 as unknown as JsonSerializable;
  const t8 = workspaceContext != null ? "enabled" : "disabled";
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = {
      description: "The workspace the user is currently working in.",
      value: t7,
      available: t8
    };
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  useCopilotAgentContext(t9);
  let t10;
  if ($[30] !== userContext) {
    t10 = userContext ?? {};
    $[30] = userContext;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const t11 = t10 as unknown as JsonSerializable;
  const t12 = userContext != null ? "enabled" : "disabled";
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = {
      description: "The signed-in user currently using Alvie AI.",
      value: t11,
      available: t12
    };
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  useCopilotAgentContext(t13);
  let t14;
  if ($[35] !== userInstructionsContext) {
    t14 = userInstructionsContext ?? {};
    $[35] = userInstructionsContext;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = t14 as unknown as JsonSerializable;
  const t16 = userInstructionsContext != null ? "enabled" : "disabled";
  let t17;
  if ($[37] !== t15 || $[38] !== t16) {
    t17 = {
      description: "Custom instructions from the signed-in user for how Alvie AI should respond. Follow these when present.",
      value: t15,
      available: t16
    };
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  useCopilotAgentContext(t17);
  let t18;
  if ($[40] !== workspaceInstructionsContext) {
    t18 = workspaceInstructionsContext ?? {};
    $[40] = workspaceInstructionsContext;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = t18 as unknown as JsonSerializable;
  const t20 = workspaceInstructionsContext != null ? "enabled" : "disabled";
  let t21;
  if ($[42] !== t19 || $[43] !== t20) {
    t21 = {
      description: "Custom instructions for this workspace that apply to all members using Alvie AI. Follow these when present.",
      value: t19,
      available: t20
    };
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  useCopilotAgentContext(t21);
};